// Identity verification for a business application's people, embeddable
// wherever an agent actually works.
//
// This lived only on the customer page's Provider tab, which an agent reviewing
// an onboarding application had no reason to know existed — the drawer they
// were looking at never mentioned it. The panel carries everything needed to
// run verification from the application itself: the parties, their links, and
// the recovery push for an application whose submission never reached the
// provider.

import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { customersApi, type HeldFile, type PartyEvidence } from '../api/customers.api.js';
import { APP_LOCALE } from '../utils/locale.js';

import { DuplicatePersonAttachPanel } from './DuplicatePersonAttachPanel.js';

const PROVIDER_COMPANY_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * What the vendor returns, made readable.
 *
 * The provider answers with nested records, and flattening them produced index
 * paths as labels — "#1 Status", "#1 #1 Status", "#2 #2 First name" — with the
 * same fact repeated once per path it could be reached by. A reviewer was being
 * handed the raw response and asked to do the reading.
 *
 * Two things go. Lifecycle statuses, because Pending and Verified both appear
 * for one check depending which sub-record you land on, and showing both invites
 * the reader to believe a finished check is outstanding — the decision above the
 * list is the answer to that question. And exact duplicates, because one first
 * name arriving under six paths is one fact, not six.
 */
function tidyFields(
  fields: readonly { label: string; value: string; group: string }[],
): { label: string; value: string; group: string }[] {
  const seen = new Set<string>();
  const out: { label: string; value: string; group: string }[] = [];
  for (const field of fields) {
    const label = field.label.replace(/#\d+\s*/g, '').trim();
    if (label === '' || /status/i.test(label)) continue;
    const value = field.value.trim();
    if (value === '') continue;
    const key = `${field.group}|${label}|${value}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ label, value, group: field.group });
  }
  return out;
}

export function PartyVerificationPanel({
  customerId,
  allowManage = false,
}: {
  customerId: string;
  /**
   * Reveals the destructive Remove action. Off by default: removal deletes the
   * person's provider profile and any identity check they passed, which is
   * evidence leaving the file — it belongs behind an explicit edit mode, not in
   * the reviewer's normal flow.
   */
  allowManage?: boolean;
}): JSX.Element {
  const {
    data,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['verification-parties', customerId],
    queryFn: async () => customersApi.verificationParties(customerId),
    enabled: customerId !== '',
    retry: false,
  });

  const [links, setLinks] = useState<Record<string, string>>({});
  const [linkError, setLinkError] = useState<string>('');
  const [busy, setBusy] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const [pushState, setPushState] = useState<'idle' | 'busy' | 'done' | 'partial' | 'error'>(
    'idle',
  );
  const [pushMessage, setPushMessage] = useState<string>('');
  const [providerCompanyId, setProviderCompanyId] = useState('');
  const [removeBusyId, setRemoveBusyId] = useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = useState<
    { providerId: string; fullName: string; decision: string } | null
  >(null);
  const [removeReason, setRemoveReason] = useState('');

  // The record behind a decision. Fetched on demand, never cached in the row:
  // this is identity-document data and every read is audit-logged server-side.
  const [evidence, setEvidence] = useState<PartyEvidence | null>(null);
  const [evidenceFor, setEvidenceFor] = useState<string | null>(null);
  const [evidenceError, setEvidenceError] = useState('');
  // The artefacts the hosted check itself produced — the captured document, the
  // liveness photo, the verification report. Fetched with the evidence so a
  // reviewer sees immediately that they exist; the bytes are pulled only when
  // one is opened, because each fetch is an audited access to identity images.
  const [files, setFiles] = useState<HeldFile[]>([]);
  const [fileUrls, setFileUrls] = useState<Record<string, string>>({});
  const [fileBusyId, setFileBusyId] = useState<string | null>(null);
  const [fileError, setFileError] = useState('');
  const [importState, setImportState] = useState<
    'idle' | 'importing' | 'held' | 'partial' | 'failed'
  >('idle');
  const [importNote, setImportNote] = useState('');
  /**
   * What the import made of each file, kept rather than discarded.
   *
   * The result was thrown away, so a file the provider would not release simply
   * never appeared and nothing anywhere said why — which is indistinguishable,
   * to a reviewer, from us never having asked for it.
   */
  const [importOutcomes, setImportOutcomes] = useState<
    { name: string; outcome: string; detail?: string }[]
  >([]);
  const [reportUrl, setReportUrl] = useState<string | null>(null);
  const [reportHtml, setReportHtml] = useState<string>('');
  const [reportBusy, setReportBusy] = useState(false);
  const [reportError, setReportError] = useState('');
  const [reportOpen, setReportOpen] = useState(false);

  const openEvidence = async (personId: string): Promise<void> => {
    setEvidenceFor(personId);
    setEvidence(null);
    setEvidenceError('');
    setReportUrl(null);
    setFiles([]);
    setFileUrls({});
    setFileError('');
    setImportState('idle');
    setImportNote('');
    setImportOutcomes([]);
    setReportError('');
    let loaded: PartyEvidence | null = null;
    try {
      loaded = await customersApi.partyEvidence(customerId, personId);
      setEvidence(loaded);
    } catch (error: unknown) {
      setEvidenceError(
        error instanceof Error ? error.message : 'Could not read the verification record.',
      );
    }
    try {
      // Our own vault first. The provider is consulted only when we hold
      // nothing yet — re-fetching a passport from a third party every time a
      // reviewer opens a record is precisely the dependency the import exists
      // to remove.
      let held = await customersApi.heldPartyFiles(customerId, personId);
      const atProvider = await customersApi.partyFiles(customerId, personId);

      /**
       * Import whenever the provider has check artefacts, not only when we hold
       * nothing.
       *
       * The condition used to be "held.length === 0", so the very first import
       * closed the door: anything that appeared afterwards — a re-run check, or
       * the verification report, which is generated rather than stored and so
       * could never be fetched from document storage — was never asked for
       * again. Three files landed, the report did not, and no later visit would
       * ever try.
       *
       * Running it every time is safe: the import deduplicates on the content
       * hash, so a file already held comes back as ALREADY_HELD rather than a
       * second copy.
       */
      if (atProvider.some((file) => file.fromVerification)) {
        setImportState('importing');
        try {
          const result = await customersApi.importPartyFiles(
            customerId,
            personId,
            loaded?.fullName ?? '',
          );
          setImportOutcomes(result.files);
          held = await customersApi.heldPartyFiles(customerId, personId);
          setImportState(result.unavailable > 0 ? 'partial' : 'held');
        } catch {
          setImportState('failed');
        }
      } else {
        setImportState(held.length > 0 ? 'held' : 'idle');
      }
      setFiles(held);
    } catch {
      // The record still stands without its attachments; the dialog says so.
      setFiles([]);
    }
  };

  const openFile = async (file: HeldFile): Promise<string | null> => {
    const existing = fileUrls[file.id];
    if (existing !== undefined) return existing;
    setFileBusyId(file.id);
    setFileError('');
    try {
      const blob = await customersApi.heldFileBlob(customerId, file.id);
      const url = URL.createObjectURL(blob);
      setFileUrls((prev) => ({ ...prev, [file.id]: url }));
      return url;
    } catch (error: unknown) {
      // Scoped to the file row: a document that will not load must not blank
      // out the verification record the reviewer came here to read.
      setFileError(
        error instanceof Error ? error.message : 'Could not open that file from the vault.',
      );
      return null;
    } finally {
      setFileBusyId(null);
    }
  };

  const downloadFile = async (file: HeldFile): Promise<void> => {
    // Works without viewing first: a reviewer filing evidence should not have
    // to render a 3 MB passport on screen to save it.
    const url = await openFile(file);
    if (url === null) return;
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = file.filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  };

  // The provider's own KYC review document. Fetched only when asked for: it is
  // a ~30 KB document, and pulling it on every dialog open would be an audited
  // access nobody requested.
  const loadReport = async (): Promise<string | null> => {
    if (evidenceFor === null) return null;
    if (reportUrl !== null) return reportUrl;
    setReportBusy(true);
    setReportError('');
    try {
      const blob = await customersApi.partyReport(customerId, evidenceFor);
      const url = URL.createObjectURL(blob);
      // Held both ways on purpose: the object URL is what a download saves,
      // and the text is what the sandboxed frame renders through srcdoc —
      // a fully sandboxed frame loading a blob: URL is browser-dependent.
      setReportHtml(await blob.text());
      setReportUrl(url);
      return url;
    } catch (error: unknown) {
      setReportError(
        error instanceof Error ? error.message : 'Could not fetch the provider report.',
      );
      return null;
    } finally {
      setReportBusy(false);
    }
  };

  const downloadReport = async (): Promise<void> => {
    const url = await loadReport();
    if (url === null) return;
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `KYC report - ${evidence?.fullName ?? 'party'}.html`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  };

  const closeEvidence = (): void => {
    if (reportUrl !== null) URL.revokeObjectURL(reportUrl);
    for (const url of Object.values(fileUrls)) URL.revokeObjectURL(url);
    setFileUrls({});
    setFiles([]);
    setReportUrl(null);
    setReportHtml('');
    setReportOpen(false);
    setEvidenceFor(null);
  };

  const issue = async (personId: string): Promise<void> => {
    setBusy(personId);
    setLinkError('');
    try {
      const result = await customersApi.issuePartyLink(customerId, personId);
      setLinks((prev) => ({ ...prev, [personId]: result.verificationLink }));
    } catch (error: unknown) {
      setLinkError(error instanceof Error ? error.message : 'Could not issue a link.');
    } finally {
      setBusy(null);
    }
  };

  const copy = async (personId: string, link: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(personId);
      window.setTimeout(() => setCopied(null), 2000);
    } catch {
      // Clipboard blocked — the link is on screen and selectable.
    }
  };

  const pushToProvider = async (force = false, existingCompanyId?: string): Promise<void> => {
    setPushState('busy');
    setPushMessage('');
    try {
      const result = await customersApi.createProviderProfile(customerId, {
        ...(force ? { force: true } : {}),
        ...(existingCompanyId !== undefined ? { companyProviderId: existingCompanyId } : {}),
      });
      const verb = result.attachedToExisting ? 'completed' : 'created';
      const reused =
        result.personsReused > 0
          ? ` ${String(result.personsReused)} existing profile(s) reused — no duplicates created.`
          : '';
      if (result.detailSynced) {
        setPushState('done');
        setPushMessage(
          `Company ${verb} at the provider (${result.providerId}): ` +
            `${String(result.directorsAdded)} director(s), ` +
            `${String(result.beneficialOwnersAdded)} owner(s). Company details synced.${reused}`,
        );
      } else {
        setPushState('partial');
        setPushMessage(
          `Company ${verb} at the provider (${result.providerId}), but the company ` +
            `details did NOT sync: ${result.detailSyncError ?? 'unknown reason'}.${reused}`,
        );
      }
      setProviderCompanyId('');
      await refetch();
    } catch (error: unknown) {
      setPushState('error');
      // "13 INTERNAL: ..." is a gRPC status code, not something an agent
      // should be asked to read.
      const raw = error instanceof Error ? error.message : 'Could not reach the provider.';
      setPushMessage(raw.replace(/^\d+\s+[A-Z_]+:\s*/, ''));
      // A refused duplicate person is kept server-side with its candidates;
      // read it back so the attach panel appears without a reload.
      void refetch();
    }
  };

  const confirmRemoval = async (): Promise<void> => {
    if (removeTarget === null || removeReason.trim().length < 4) return;
    setRemoveBusyId(removeTarget.providerId);
    setLinkError('');
    try {
      await customersApi.removeParty(customerId, removeTarget.providerId, removeReason.trim());
      setRemoveTarget(null);
      setRemoveReason('');
      await refetch();
    } catch (error: unknown) {
      setLinkError(error instanceof Error ? error.message : 'Could not remove the party.');
    } finally {
      setRemoveBusyId(null);
    }
  };

  const parties = data?.parties ?? [];
  const needsCompanyRelink =
    pushState === 'error' && /already exists|POST \/companies failed with 501/i.test(pushMessage);

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Identity verification
      </Typography>

      {isError ? (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          The parties list could not be loaded — this is not evidence the customer has none.
        </Alert>
      ) : null}

      {!isError && parties.length === 0 ? (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          No people are recorded at the identity provider. Send the application below — if
          the company already exists there, the people are attached to it; otherwise the
          company is created whole. Links can be issued here afterwards.
        </Alert>
      ) : null}

      {/* A person the provider refused as a duplicate: the comparison and the
          staff override that attaches the existing profile (DEV-126). */}
      <DuplicatePersonAttachPanel
        customerId={customerId}
        refusals={data?.refusals ?? []}
        onAttached={async () => {
          setPushState('idle');
          setPushMessage('');
          await refetch();
        }}
      />

      {parties.length > 0 ? (
        <Table size="small" sx={{ mb: 1.5 }}>
          <TableHead>
            <TableRow>
              <TableCell>Party</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Identity check</TableCell>
              <TableCell>Verification link</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {parties.map((party) => {
              // The stored link first: it survives refreshes and other agents,
              // and showing it means nobody re-mints a fresh verification at
              // the vendor just by opening this drawer again.
              const link = links[party.providerId] ?? (party.verificationLink || undefined);
              const decision = party.identityDecision || 'PENDING';
              return (
                <TableRow key={party.providerId}>
                  <TableCell>{party.fullName}</TableCell>
                  <TableCell>{party.role}</TableCell>
                  <TableCell>
                    {/* Read back from the provider on each open — until this,
                        the only way to learn a director had finished their
                        check was the vendor console. */}
                    <Chip
                      size="small"
                      label={decision}
                      color={
                        decision === 'APPROVED'
                          ? 'success'
                          : decision === 'REJECTED'
                            ? 'error'
                            : decision === 'EDD_REQUIRED'
                              ? 'warning'
                              : 'default'
                      }
                      title={party.identityStatus}
                    />
                  </TableCell>
                  <TableCell>
                    {decision === 'APPROVED' ? (
                      // A passed check must be inspectable — otherwise the file
                      // records a claim with nothing behind it.
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => void openEvidence(party.providerId)}
                      >
                        View check
                      </Button>
                    ) : link !== undefined ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography
                          variant="caption"
                          sx={{ wordBreak: 'break-all', maxWidth: 320 }}
                        >
                          {link}
                        </Typography>
                        <Button size="small" onClick={() => void copy(party.providerId, link)}>
                          {copied === party.providerId ? 'Copied' : 'Copy'}
                        </Button>
                      </Stack>
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={busy === party.providerId}
                        onClick={() => void issue(party.providerId)}
                      >
                        {busy === party.providerId ? 'Issuing…' : 'Issue link'}
                      </Button>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {/* Only in edit mode: this deletes the person at the
                        provider, taking any passed check with them. */}
                    {allowManage ? (
                      <Button
                        size="small"
                        color="error"
                        disabled={removeBusyId === party.providerId}
                        onClick={() =>
                          setRemoveTarget({
                            providerId: party.providerId,
                            fullName: party.fullName,
                            decision,
                          })
                        }
                      >
                        {removeBusyId === party.providerId ? 'Removing…' : 'Remove'}
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      ) : null}

      {linkError !== '' ? (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {linkError}
          {/* The named stale-records case has a way out; offer it where the
              failure appears rather than sending the agent to find a button
              that is hidden while parties exist. */}
          {/previous provider configuration/i.test(linkError) ? (
            <Box sx={{ mt: 1 }}>
              <Button
                size="small"
                color="inherit"
                variant="outlined"
                disabled={pushState === 'busy'}
                onClick={() => void pushToProvider()}
              >
                {pushState === 'busy' ? 'Re-sending…' : 'Replace records and re-send'}
              </Button>
            </Box>
          ) : null}
        </Alert>
      ) : null}

      {/* The push is the recovery path for an application that never reached
          the provider. Once parties exist the company is there — offering the
          button anyway invited a duplicate push the vendor refuses. */}
      {parties.length === 0 ? (
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
          <Button
            size="small"
            variant="outlined"
            disabled={pushState === 'busy'}
            onClick={() => void pushToProvider()}
          >
            {pushState === 'busy' ? 'Sending…' : 'Send application to provider'}
          </Button>
          <Typography variant="caption" color="text.secondary">
            Creates the company — or completes it, if it already exists at the provider.
          </Typography>
        </Stack>
      ) : null}
      {pushMessage !== '' ? (
        <Alert
          severity={
            pushState === 'error' ? 'error' : pushState === 'partial' ? 'warning' : 'success'
          }
        >
          {pushMessage}
          {needsCompanyRelink ? (
            <Stack spacing={1.25} sx={{ mt: 1.5, maxWidth: 560 }}>
              <Typography variant="body2">
                Open this company in Platforma365, copy the UUID from its URL, and attach that
                existing record. Norvix verifies the ID before writing anything.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField
                  size="small"
                  label="Platforma365 company ID"
                  value={providerCompanyId}
                  onChange={(event) => setProviderCompanyId(event.target.value.trim())}
                  error={providerCompanyId !== '' && !PROVIDER_COMPANY_ID_RE.test(providerCompanyId)}
                  sx={{ flexGrow: 1 }}
                />
                <Button
                  variant="contained"
                  color="inherit"
                  disabled={!PROVIDER_COMPANY_ID_RE.test(providerCompanyId)}
                  onClick={() => void pushToProvider(false, providerCompanyId)}
                >
                  Attach and continue
                </Button>
              </Stack>
            </Stack>
          ) : null}
        </Alert>
      ) : null}

      {/* The record behind the decision: what the provider checked, against
          which profile, and when we read it. */}
      <Dialog
        open={evidenceFor !== null}
        onClose={closeEvidence}
        maxWidth={reportOpen ? 'lg' : 'sm'}
        fullWidth
      >
        <DialogTitle>Identity check — {evidence?.fullName ?? 'party'}</DialogTitle>
        <DialogContent dividers>
          {/* A failure banner sits ABOVE the record, never instead of it: one
              attachment the provider will not serve is no reason to hide the
              verification a reviewer opened this dialog to read. */}
          {evidenceError !== '' ? (
            <Alert severity={evidence === null ? 'error' : 'warning'} sx={{ mb: 1.5 }}>
              {evidenceError}
            </Alert>
          ) : null}
          {reportError !== '' ? (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              {reportError}
            </Alert>
          ) : null}
          {evidence === null ? (
            <Typography variant="body2" color="text.secondary">
              {evidenceError === '' ? 'Reading the record from the provider…' : null}
            </Typography>
          ) : (
            <>
              <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} alignItems="center">
                <Chip
                  size="small"
                  color={evidence.decision === 'APPROVED' ? 'success' : 'default'}
                  label={evidence.decision}
                />
                <Typography variant="caption" color="text.secondary">
                  {evidence.providerStatus} · {String(evidence.checkCount)} check(s) on file · read{' '}
                  {new Date(evidence.readAt).toLocaleString(APP_LOCALE)}
                </Typography>
              </Stack>
              {tidyFields(evidence.fields).length === 0 ? (
                <Alert severity="info">
                  The provider returned no detail for this check beyond its status. Open the
                  provider console for the full record.
                </Alert>
              ) : (
                [...new Set(tidyFields(evidence.fields).map((field) => field.group))].map((group) => (
                  <Box key={group} sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {group}
                    </Typography>
                    {tidyFields(evidence.fields)
                      .filter((field) => field.group === group)
                      .map((field) => (
                        <Box
                          key={`${group}-${field.label}-${field.value}`}
                          sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.25 }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            {field.label}
                          </Typography>
                          <Typography variant="body2" sx={{ textAlign: 'right', wordBreak: 'break-word' }}>
                            {field.value}
                          </Typography>
                        </Box>
                      ))}
                  </Box>
                ))
              )}
              {/* The provider's own review document, rendered in a sandboxed
                  frame: it is provider HTML built around customer-supplied
                  names, so it never runs as markup on the admin origin. */}
              {reportOpen && reportHtml !== '' ? (
                <Box
                  component="iframe"
                  srcDoc={reportHtml}
                  title="Provider KYC review report"
                  sandbox=""
                  sx={{
                    width: '100%',
                    height: 520,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: '#fff',
                    mb: 1,
                  }}
                />
              ) : null}
              {files.length > 0 ? (
                <>
                  <Typography variant="subtitle2" gutterBottom>
                    Captured by the check — held by Norvix
                  </Typography>
                  {importState === 'importing' ? (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      First open — copying the check's files into Norvix's own vault…
                    </Typography>
                  ) : importOutcomes.length > 0 ? (
                    <Alert
                      severity={importState === 'partial' ? 'warning' : 'success'}
                      sx={{ mb: 1 }}
                    >
                      {/* Every file the provider listed, and what became of it.
                          A file that could not be fetched used to leave no
                          trace at all — indistinguishable, to a reviewer, from
                          one we never asked for. */}
                      {importOutcomes.map((f) => (
                        <Typography key={f.name} variant="caption" display="block">
                          {f.outcome === 'IMPORTED'
                            ? '✓ held by Norvix'
                            : f.outcome === 'ALREADY_HELD'
                              ? '· already held'
                              : '✗ the provider would not release it'}
                          {' — '}
                          {f.name}
                          {f.detail !== undefined && f.detail !== '' ? ` (${f.detail})` : ''}
                        </Typography>
                      ))}
                    </Alert>
                  ) : importNote !== '' ? (
                    <Alert
                      severity={importState === 'partial' ? 'warning' : 'success'}
                      sx={{ mb: 1 }}
                    >
                      {importNote} They are reviewable with the customer's other documents and no
                      longer depend on the provider.
                    </Alert>
                  ) : null}
                  {fileError !== '' ? (
                    <Alert severity="warning" sx={{ mb: 1 }}>
                      {fileError}
                    </Alert>
                  ) : null}
                  {/* Held by Norvix now: the bytes below come from our own
                      vault, not from a live call to the provider. */}
                  {files.map((file) => {
                    const url = fileUrls[file.id];
                    const isImage = /passport|photo|selfie|liveness|document/i.test(file.filename);
                    return (
                      <Box key={file.id} sx={{ mb: 1.5 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2" sx={{ flexGrow: 1, minWidth: 0 }} noWrap>
                            {file.filename}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {`${String(Math.max(Math.round(file.sizeBytes / 1024), 1))} KB`}
                          </Typography>
                          <Button
                            size="small"
                            disabled={fileBusyId === file.id}
                            onClick={() => void openFile(file)}
                          >
                            {fileBusyId === file.id ? 'Opening…' : url === undefined ? 'View' : 'Shown'}
                          </Button>
                          <Button
                            size="small"
                            disabled={fileBusyId === file.id}
                            onClick={() => void downloadFile(file)}
                          >
                            Download
                          </Button>
                        </Stack>
                        {url !== undefined ? (
                          <Box
                            sx={{
                              mt: 1,
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                              overflow: 'hidden',
                              maxHeight: 460,
                              bgcolor: 'background.default',
                            }}
                          >
                            {isImage ? (
                              <Box
                                component="img"
                                src={url}
                                alt={file.filename}
                                sx={{ width: '100%', maxHeight: 460, objectFit: 'contain' }}
                              />
                            ) : (
                              <Box
                                component="iframe"
                                src={url}
                                title={file.filename}
                                sx={{ width: '100%', height: 460, border: 0 }}
                              />
                            )}
                          </Box>
                        ) : null}
                      </Box>
                    );
                  })}
                </>
              ) : (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  The provider holds no files for this person — a check that has not completed
                  produces none.
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary">
                The verification record above is read live from the provider. The files are
                served from Norvix's own vault; viewing one is audit-logged.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {evidence !== null ? (
            <>
              <Button
                disabled={reportBusy}
                onClick={() => {
                  void (async () => {
                    const url = await loadReport();
                    if (url !== null) setReportOpen((open) => !open);
                  })();
                }}
              >
                {reportBusy ? 'Fetching…' : reportOpen ? 'Hide full report' : 'Full KYC report'}
              </Button>
              <Button disabled={reportBusy} onClick={() => void downloadReport()}>
                Download report
              </Button>
            </>
          ) : null}
          {evidence?.providerConsoleUrl !== null &&
          evidence?.providerConsoleUrl !== undefined &&
          evidence.providerConsoleUrl !== '' ? (
            <Button
              component="a"
              href={evidence.providerConsoleUrl}
              target="_blank"
              rel="noreferrer noopener"
            >
              Open in provider console
            </Button>
          ) : null}
          <Button onClick={closeEvidence}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* A typed reason, not a browser confirm: this destroys evidence, and the
          reason is written into the audit record. */}
      <Dialog open={removeTarget !== null} onClose={() => setRemoveTarget(null)}>
        <DialogTitle>Remove {removeTarget?.fullName} from the provider?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This unlinks them from the company and deletes their profile at the provider.
            {removeTarget?.decision === 'APPROVED'
              ? ' Their completed identity check is deleted with it and cannot be restored — they would have to verify again.'
              : ' Any verification in progress is lost.'}
          </Alert>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            label="Reason (required)"
            value={removeReason}
            onChange={(event) => setRemoveReason(event.target.value)}
            helperText="Recorded in the audit log, e.g. “duplicate profile created by a re-send”."
            sx={{ minWidth: 380 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveTarget(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={removeReason.trim().length < 4 || removeBusyId !== null}
            onClick={() => void confirmRemoval()}
          >
            {removeBusyId !== null ? 'Removing…' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
